import { useState } from 'react';
import { Typography, message, Modal } from 'antd';
import { SaveOutlined, GlobalOutlined, CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { businessApi } from '../../api/businessApi';
import { useBusinessStore } from '../../store/businessStore';
import { telegram } from '../../utils/telegram';
import { LANGUAGE_OPTIONS } from '../../i18n/languageOptions';
import SettingsInfoButton from './SettingsInfoButton';

export default function LanguageSection() {
  const { t } = useTranslation(['common', 'owner']);
  const { selectedBusiness, updateSelectedBusiness } = useBusinessStore();
  if (!selectedBusiness) return null;

  const [supported, setSupported] = useState<string[]>(selectedBusiness.supportedLanguages);
  const [defaultLang, setDefaultLang] = useState(selectedBusiness.defaultLanguage);
  const [saving, setSaving] = useState(false);

  const hasChanges =
    JSON.stringify(supported.slice().sort()) !== JSON.stringify([...selectedBusiness.supportedLanguages].sort()) ||
    defaultLang !== selectedBusiness.defaultLanguage;

  const handleToggleSupported = (val: string) => {
    const isOn = supported.includes(val);
    if (isOn && supported.length === 1) return; // kamida 1 ta til saqlanishi kerak

    if (isOn) {
      Modal.confirm({
        title: t('owner:settings.language.removeTitle'),
        content: t('owner:settings.language.removeWarning'),
        okText: t('owner:settings.language.removeConfirm'),
        okButtonProps: { danger: true },
        cancelText: t('owner:settings.language.removeCancel'),
        onOk: () => {
          const next = supported.filter((l) => l !== val);
          setSupported(next);
          if (!next.includes(defaultLang)) setDefaultLang(next[0]);
        },
      });
    } else {
      const next = [...supported, val];
      setSupported(next);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await businessApi.updateTenant(selectedBusiness.id, {
        defaultLanguage: defaultLang,
        supportedLanguages: supported,
      });
      updateSelectedBusiness(updated);
      telegram.hapticFeedback.notification('success');
      void message.success(t('owner:settings.language.saved'));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      void message.error(error?.response?.data?.message ?? t('common:errors.saveFailed'));
      telegram.hapticFeedback.notification('error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ borderRadius: 16, background: 'var(--bl-surface, #fff)', border: '1px solid var(--bl-divider, #ebebeb)', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '12px 14px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, borderBottom: '1px solid var(--bl-divider, #ebebeb)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#d97706' }}>
            <GlobalOutlined />
          </div>
          <Typography.Text style={{ fontSize: 15, fontWeight: 700, color: 'var(--bl-text-strong, #0f172a)' }}>
            {t('owner:settings.language.title')}
          </Typography.Text>
        </div>
        <SettingsInfoButton
          title={t('owner:settings.language.info.title')}
          body={t('owner:settings.language.info.body')}
        />
      </div>

      <div style={{ padding: '14px 14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Supported languages */}
        <div>
          <Typography.Text style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--bl-text-muted, #6b7280)', marginBottom: 8 }}>
            {t('owner:settings.language.supported')}
          </Typography.Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {LANGUAGE_OPTIONS.map((lang) => {
              const isOn = supported.includes(lang.value);
              return (
                <button
                  key={lang.value}
                  type="button"
                  onClick={() => handleToggleSupported(lang.value)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 12, border: isOn ? '2px solid #d97706' : '1px solid var(--bl-divider, #ebebeb)', background: isOn ? '#fef3c7' : 'var(--bl-surface, #fff)', cursor: 'pointer', fontFamily: 'inherit', WebkitTapHighlightColor: 'transparent', textAlign: 'left' }}
                >
                  <span style={{ fontSize: 20 }}>{lang.flag}</span>
                  <Typography.Text style={{ flex: 1, fontSize: 14, fontWeight: 600, color: isOn ? '#92400e' : 'var(--bl-text-strong, #0f172a)' }}>
                    {lang.label}
                  </Typography.Text>
                  {isOn && (
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', flexShrink: 0 }}>
                      <CheckOutlined />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Default language */}
        {supported.length > 1 && (
          <div>
            <Typography.Text style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--bl-text-muted, #6b7280)', marginBottom: 8 }}>
              {t('owner:settings.language.default')}
            </Typography.Text>
            <div style={{ display: 'flex', gap: 8 }}>
              {LANGUAGE_OPTIONS.filter((l) => supported.includes(l.value)).map((lang) => {
                const isDefault = defaultLang === lang.value;
                return (
                  <button
                    key={lang.value}
                    type="button"
                    onClick={() => setDefaultLang(lang.value)}
                    style={{ flex: 1, height: 40, borderRadius: 12, border: isDefault ? '2px solid #d97706' : '1px solid var(--bl-divider, #ebebeb)', background: isDefault ? '#fef3c7' : 'var(--bl-surface, #fff)', cursor: 'pointer', fontFamily: 'inherit', WebkitTapHighlightColor: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                  >
                    <span style={{ fontSize: 16 }}>{lang.flag}</span>
                    <Typography.Text style={{ fontSize: 13, fontWeight: isDefault ? 700 : 500, color: isDefault ? '#92400e' : 'var(--bl-text-muted, #6b7280)' }}>
                      {lang.label}
                    </Typography.Text>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {hasChanges && (
          <button type="button" onClick={() => void handleSave()} disabled={saving} style={{ width: '100%', height: 46, borderRadius: 12, background: saving ? '#fef3c7' : '#d97706', border: 'none', color: saving ? '#92400e' : '#fff', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: saving ? 0.7 : 1 }}>
            <SaveOutlined /> {saving ? t('common:actions.saving') : t('common:actions.save')}
          </button>
        )}
      </div>
    </div>
  );
}
